import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Search as SearchIcon, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ListTools } from "@/components/ListToolbar";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import { Badge } from "@/components/ui";
import { NcrPanel } from "@/features/quality/NcrPanel";
import {
  isNcrFilter,
  listNcrs,
  ncrDetailQuery,
  ncrFilters,
  ncrListQuery,
  ncrTone,
  type NcrFilter,
  type NcrRow,
} from "@/features/quality/ncr";
import { activeCompanyOf, useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { useFormat } from "@/lib/format";
import { sessionContextQuery, useCan } from "@/lib/session";
import { ncrListColumns, ncrStatuses } from "@patronix/domain/list";
import { listView, noView, viewQuery, viewSearch, type ViewSearch } from "@/lib/listView";

// Quality → Nonconformance reports of the active company: from rejected
// inspections or reported by hand. Keyset (created desc) through ncr_page;
// detail = one document RPC, prefetched on hover. Documents created by
// functions from stock on hand: export only, no import (ADR-034).

interface Search extends ViewSearch {
  status: NcrFilter;
  q: string;
  id?: string | undefined;
}

export const Route = createFileRoute("/_app/quality/nonconformances")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = {
      status: isNcrFilter(raw.status) ? raw.status : "open",
      q: typeof raw.q === "string" ? raw.q.slice(0, 100) : "",
    };
    if (typeof raw.id === "string" && raw.id) s.id = raw.id;
    return { ...s, ...viewSearch(ncrListColumns, raw) };
  },
  loaderDeps: ({ search }) => ({ status: search.status, q: search.q, f: search.f, s: search.s }),
  loader: async ({ context, deps }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionContextQuery);
    const companyId = activeCompanyOf(ctx)?.id;
    if (companyId) await context.queryClient.ensureInfiniteQueryData(ncrListQuery({ companyId, status: deps.status, q: deps.q, ...viewQuery(ncrListColumns, deps) }));
  },
  component: NcrPage,
});

function NcrPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const canManage = useCan("quality.ncr.manage");
  const { company } = useActiveCompany();
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const view = listView(ncrListColumns, search, setSearch);
  const params = { companyId: company?.id ?? "", status: search.status, q: search.q, ...view.query };
  const query = useInfiniteQuery(ncrListQuery(params));
  const [text, setText] = useState(search.q);
  useEffect(() => {
    if (text === search.q) return;
    const id = setTimeout(() => setSearch({ q: text, id: undefined }), 200);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  const columns: Column<NcrRow>[] = [
    {
      key: "number",
      header: t("quality.ins.colNumber"),
      width: "minmax(130px,0.8fr)",
      cell: (r, selected) => <span className={cn("font-mono text-xs", selected && "font-semibold text-accent-ink")}>{r.number}</span>,
    },
    {
      key: "item",
      header: t("quality.plan.item"),
      width: "minmax(180px,1.5fr)",
      cell: (r) => (
        <span className="truncate">
          <span className="font-mono text-xs">{r.item_code}</span> <span className="text-ink-soft">{r.item_description}</span>
        </span>
      ),
    },
    { key: "lot", header: t("quality.ins.lot"), width: "minmax(90px,0.7fr)", cell: (r) => <span className="font-mono text-xs">{r.lot_code ?? "—"}</span> },
    {
      key: "qty",
      header: t("quality.ins.qty"),
      width: "minmax(90px,0.6fr)",
      align: "end",
      cell: (r) => (
        <span className="tabular">
          {fmt.qty(String(r.qty))} {r.base_uom_code}
        </span>
      ),
    },
    {
      key: "where",
      header: t("quality.ins.where"),
      width: "minmax(120px,0.8fr)",
      className: "text-ink-soft",
      cell: (r) => (
        <span className="font-mono text-xs">
          {r.warehouse_code} · {r.location_code}
        </span>
      ),
    },
    {
      key: "source",
      header: t("quality.ncr.inspection"),
      width: "minmax(110px,0.7fr)",
      className: "text-ink-soft",
      cell: (r) => (r.inspection_number ? <span className="font-mono text-xs">{r.inspection_number}</span> : t("quality.ncr.manual")),
    },
    {
      key: "status",
      header: t("po.colStatus"),
      width: "minmax(120px,0.8fr)",
      cell: (r) => (
        <Badge tone={ncrTone[r.status]}>
          {r.disposition && r.status !== "cancelled" ? t(`quality.ncr.disposition.${r.disposition}`) : t(`quality.ncr.status.${r.status}`)}
        </Badge>
      ),
      options: ncrStatuses.map((v) => ({ value: v, label: t(`quality.ncr.status.${v}`) })),
    },
  ];
  const exportColumns: ExportColumn<NcrRow>[] = [
    { header: t("quality.ins.colNumber"), kind: "code", value: (r) => r.number },
    { header: "item_code", kind: "code", value: (r) => r.item_code },
    { header: t("stock.colDescription"), kind: "text", value: (r) => r.item_description },
    { header: "lot", kind: "code", value: (r) => r.lot_code },
    { header: "qty", kind: "number", value: (r) => Number(r.qty), unit: (r) => r.base_uom_code },
    { header: "uom", kind: "code", value: (r) => r.base_uom_code },
    { header: "warehouse", kind: "code", value: (r) => r.warehouse_code },
    { header: "location", kind: "code", value: (r) => r.location_code },
    { header: "inspection", kind: "code", value: (r) => r.inspection_number },
    { header: "supplier", kind: "code", value: (r) => r.supplier_code },
    { header: "status", kind: "code", value: (r) => r.status },
    { header: "disposition", kind: "code", value: (r) => r.disposition },
    { header: "created_at", kind: "datetime", value: (r) => r.created_at },
  ];
  const label = (f: NcrFilter) => (f === "all" ? t("po.filterAll") : t(`quality.ncr.filter.${f}`));

  const listTools: ListTools<NcrRow> = {
    id: "nonconformance-reports",
    title: t("quality.ncr.title"),
    onNew: canManage ? () => setSearch({ id: "new" }) : undefined,
    export: { fileBase: "nonconformance-reports", sheet: t("quality.ncr.title"), columns: exportColumns, fetchPage: (cursor, signal) => listNcrs(params, cursor, signal, EXPORT_PAGE) },
  };

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.quality.title")} <span aria-hidden="true">/</span> {t("nav.quality.nonconformances")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("quality.ncr.title")}</h1>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setSearch({ id: "new" })}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-accent pl-3 pr-4 font-medium text-on-accent hover:bg-accent-hover"
          >
            <Plus size={18} strokeWidth={2} aria-hidden="true" />
            {t("quality.ncr.new")}
          </button>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label={t("po.colStatus")} className="flex flex-wrap gap-2">
          {ncrFilters.map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={search.status === f}
              onClick={() => setSearch({ status: f, id: undefined })}
              className={cn(
                "h-8 rounded-full border px-3 text-[13px] transition-colors",
                search.status === f ? "border-ink bg-ink font-medium text-surface" : "border-border bg-surface text-ink hover:bg-surface-hover",
              )}
            >
              {label(f)}
            </button>
          ))}
        </div>
        <div className="grow" />
        <label className="flex h-9 min-w-[240px] items-center gap-2 rounded-xl border border-border bg-surface px-3 text-ink-muted focus-within:outline-2 focus-within:outline-focus">
          <SearchIcon size={15} aria-hidden="true" />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("quality.ncr.searchPlaceholder")}
            aria-label={t("quality.ncr.searchPlaceholder")}
            data-list-search
            className="min-w-0 grow bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-muted"
          />
          {text && (
            <button type="button" aria-label={t("po.clearFilters")} onClick={() => setText("")} className="text-ink-muted">
              <X size={14} />
            </button>
          )}
        </label>
      </div>

      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("quality.ncr.listLabel")}
          columns={columns}
          query={query}
          rowId={(r) => r.id}
          selectedId={search.id}
          onSelect={(r) => setSearch({ id: r.id })}
          onClear={() => setSearch({ id: undefined })}
          onPrefetch={(r) => void queryClient.prefetchQuery(ncrDetailQuery(r.id))}
          emptyTitle={t("quality.ncr.emptyTitle")}
          emptyBody={t("quality.ncr.emptyBody")}
          emptyAction={
            <button
              type="button"
              onClick={() => setSearch({ status: "all", q: "", id: undefined, ...noView })}
              className="h-9 rounded-xl border border-border px-3.5 text-[13px] font-medium hover:bg-surface-hover"
            >
              {t("list.clearFilters")}
            </button>
          }
          minWidth={860}
          tools={listTools}
          view={view}
        />
        <DetailPanel
          label={t("quality.ncr.detailLabel")}
          open={!!search.id}
          placeholder={<DetailPlaceholder title={t("quality.ncr.selectPrompt")} hint={t("po.selectPromptHint")} />}
        >
          {search.id && <NcrPanel key={search.id} id={search.id} onClose={() => setSearch({ id: undefined })} onOpen={(id) => setSearch({ id })} />}
        </DetailPanel>
      </div>
    </div>
  );
}
